import type { ReactNode } from 'react';
import { usePermission } from '@/hooks/usePermission';
import { ErrorState } from '@/components/common/ErrorState';
import { ApiError } from '@/api/errors';

/**
 * Route-level permission gate. Unlike RequireAuth (which redirects), an
 * authenticated-but-unauthorized user gets a 403 surface — redirecting them
 * would be confusing since they ARE signed in. Backend authz is authoritative;
 * this only avoids showing a page the user can't use.
 */
export function RequirePermission({ permission, anyOf, allOf, children }: {
  permission?: string;
  anyOf?: string[];
  allOf?: string[];
  children: ReactNode;
}) {
  const { can, canAny, canAll } = usePermission();
  let allowed = true;
  if (permission) allowed = allowed && can(permission);
  if (anyOf) allowed = allowed && canAny(anyOf);
  if (allOf) allowed = allowed && canAll(allOf);

  if (!allowed) {
    return (
      <div className="py-16">
        <ErrorState error={new ApiError('forbidden', 403, 'FORBIDDEN', "You don't have access to this area.")} />
      </div>
    );
  }
  return <>{children}</>;
}
